(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DreamItEditorControls = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  'use strict';

  const SUPPORTED_ROUTES = Object.freeze(['create', 'upload']);

  const positive = (value) => Number.isFinite(Number(value)) && Number(value) > 0;

  const routeSource = (route) => {
    if (route === 'upload') return Object.freeze({ adjective: 'uploaded', dimensionLabel: 'Upload size' });
    if (route === 'create') return Object.freeze({ adjective: 'generated', dimensionLabel: 'Generated size' });
    return Object.freeze({ adjective: 'source', dimensionLabel: 'Image size' });
  };

  const productCopy = (template, productName) => String(template || '').replaceAll(
    '[product]',
    String(productName || '').trim() || 'selected product'
  );

  const millimetres = (value) => Number(value).toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const pixelsToMillimetres = (pixels, ppi) => (
    positive(pixels) && positive(ppi) ? Number(pixels) * 25.4 / Number(ppi) : null
  );

  const dimensionLine = ({ label, widthPx, heightPx, widthMm = null, heightMm = null }) => {
    const exactLabel = String(label || '').trim();
    const width = Math.round(Number(widthPx));
    const height = Math.round(Number(heightPx));
    if (!exactLabel || !Number.isInteger(width) || width <= 0 || !Number.isInteger(height) || height <= 0) return '';
    const pixels = `${width.toLocaleString('en-GB')} × ${height.toLocaleString('en-GB')} px`;
    if (!positive(widthMm) || !positive(heightMm)) return `${exactLabel}: ${pixels}`;
    return `${exactLabel}: ${millimetres(widthMm)} × ${millimetres(heightMm)} mm (${pixels})`;
  };

  const dimensionCopy = ({
    route,
    sourceWidthPx,
    sourceHeightPx,
    outputWidthPx,
    outputHeightPx,
    outputWidthMm = null,
    outputHeightMm = null,
    ppi = null
  }) => {
    const source = routeSource(route);
    const hasAuthoritativeArtboard = positive(outputWidthMm) && positive(outputHeightMm);
    const hasPpi = positive(ppi);
    const finalWidthMm = hasAuthoritativeArtboard ? Number(outputWidthMm) : pixelsToMillimetres(outputWidthPx, ppi);
    const finalHeightMm = hasAuthoritativeArtboard ? Number(outputHeightMm) : pixelsToMillimetres(outputHeightPx, ppi);
    const canDeriveFromArtboard = hasAuthoritativeArtboard && positive(outputWidthPx) && positive(outputHeightPx);
    const sourceWidthMm = hasPpi
      ? pixelsToMillimetres(sourceWidthPx, ppi)
      : (canDeriveFromArtboard ? Number(sourceWidthPx) * Number(outputWidthMm) / Number(outputWidthPx) : null);
    const sourceHeightMm = hasPpi
      ? pixelsToMillimetres(sourceHeightPx, ppi)
      : (canDeriveFromArtboard ? Number(sourceHeightPx) * Number(outputHeightMm) / Number(outputHeightPx) : null);
    const hasPhysicalGeometry = positive(finalWidthMm) && positive(finalHeightMm);
    return Object.freeze({
      source: dimensionLine({
        label: source.dimensionLabel,
        widthPx: sourceWidthPx,
        heightPx: sourceHeightPx,
        widthMm: sourceWidthMm,
        heightMm: sourceHeightMm
      }),
      final: dimensionLine({
        label: 'Final size',
        widthPx: outputWidthPx,
        heightPx: outputHeightPx,
        widthMm: finalWidthMm,
        heightMm: finalHeightMm
      }),
      physicalGeometryAvailable: hasPhysicalGeometry,
      physicalGeometrySource: hasAuthoritativeArtboard ? 'artboard' : (hasPpi ? 'ppi' : 'pixels')
    });
  };

  const scaleCopy = ({ route, scale }) => {
    const percentage = Math.round(Number(scale) * 100);
    if (!Number.isFinite(percentage) || percentage <= 0) return '';
    const adjective = routeSource(route).adjective;
    if (percentage > 100) return `Image size — Enlarged to ${percentage}% of ${adjective} size`;
    if (percentage < 100) return `Image size — Reduced to ${percentage}% of ${adjective} size`;
    return `Image size — 100% of ${adjective} size`;
  };

  const routeControls = (route) => {
    const authoritativeRoute = String(route || '');
    return Object.freeze({
      useVisible: SUPPORTED_ROUTES.includes(authoritativeRoute),
      downloadVisible: SUPPORTED_ROUTES.includes(authoritativeRoute),
      replaceVisible: authoritativeRoute === 'upload',
      startAgainVisible: SUPPORTED_ROUTES.includes(authoritativeRoute)
    });
  };

  const productAction = ({ route, productName, prepared }) => {
    const authoritativeRoute = String(route || '');
    const exactProductName = String(productName || '').trim();
    const visible = SUPPORTED_ROUTES.includes(authoritativeRoute) && prepared === true && Boolean(exactProductName);
    return Object.freeze({
      visible,
      enabled: visible,
      label: exactProductName ? `Add image to ${exactProductName}` : 'Add image to product'
    });
  };

  const exactVariantTitle = (variant) => String(variant?.title || variant?.name || '').trim();

  const isDefaultVariantTitle = (title) => /^default(?:\s+title)?$/i.test(String(title || '').trim());

  const variantPicker = ({ variants = [], configuredLabel = '' } = {}) => {
    const available = Array.isArray(variants) ? variants.filter((variant) => (
      variant && variant.id && variant.available !== false
    )) : [];
    const meaningful = available.filter((variant) => !isDefaultVariantTitle(exactVariantTitle(variant)));
    const required = meaningful.length > 1;
    const trimmedLabel = String(configuredLabel || '').trim();
    return Object.freeze({
      required,
      label: trimmedLabel || 'Choose Type',
      variants: Object.freeze(required ? meaningful.slice() : [])
    });
  };

  const selectedVariant = (picker, variantId) => {
    const exactId = String(variantId || '');
    if (!picker?.required || !exactId) return null;
    return picker.variants.find((variant) => String(variant.id) === exactId) || null;
  };

  const selectedProductSummary = ({ productName, picker, variantId } = {}) => {
    const product = String(productName || '').trim();
    const variant = selectedVariant(picker, variantId);
    const title = exactVariantTitle(variant);
    return Object.freeze({
      text: product && title ? `${product} – ${title}` : product,
      variantTitle: title
    });
  };

  const selectedProductImage = ({ productImage = '', picker, variantId } = {}) => {
    const variant = selectedVariant(picker, variantId);
    const variantImage = String(
      variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || ''
    ).trim();
    return Object.freeze({
      url: variantImage || String(productImage || '').trim(),
      source: variantImage ? 'variant' : (productImage ? 'product' : 'none')
    });
  };

  const resultCopyVisibility = ({ editorVisible, heading, status } = {}) => Object.freeze({
    headingHidden: editorVisible === true || !String(heading || '').trim(),
    statusHidden: editorVisible === true || !String(status || '').trim()
  });

  return Object.freeze({
    routeControls,
    productAction,
    productCopy,
    pixelsToMillimetres,
    dimensionLine,
    dimensionCopy,
    scaleCopy,
    variantPicker,
    selectedVariant,
    selectedProductSummary,
    selectedProductImage,
    resultCopyVisibility
  });
}));
